import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Button from '@hellodarwin/core/lib/components/common/button';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import AddressAutocompleteInput from '@hellodarwin/core/lib/components/common/address-autocomplete-input';
import FormItem from '@hellodarwin/core/lib/components/common/form-item';
import FormLabel from '@hellodarwin/core/lib/components/common/form-label';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Modal from 'antd/es/modal';
import Select from 'antd/es/select';
import { useCallback, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { ModalContent, ModalFieldGroup, ModalFooter } from './styles';
const ALL_FIELDS = [
    'name',
    'size',
    'industry_sector',
    'annual_revenue',
    'legal_structure',
    'address',
];
const CompanyInfoModal = ({ open, onClose, onSave, company, fields, options, }) => {
    const { t } = useTranslations();
    const theme = useTheme();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const visibleFields = fields !== null && fields !== void 0 ? fields : ALL_FIELDS;
    const showField = useCallback((field) => visibleFields.includes(field), [visibleFields]);
    useEffect(() => {
        var _a;
        if (open) {
            form.resetFields();
            if (company) {
                form.setFieldsValue({
                    name: company.name,
                    size: company.size,
                    industry_sector: company.industry_sector,
                    annual_revenue: company.annual_revenue,
                    legal_structure: company.legal_structure,
                    country: (_a = company.country) !== null && _a !== void 0 ? _a : 'Canada',
                    province: company.province,
                    city: company.city,
                    address: company.address,
                    postal_code: company.postal_code,
                    isCanada: company.country === 'Canada' || !company.country,
                });
            }
        }
    }, [open, company, form]);
    const handleSubmit = useCallback(async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            const companyUpdate = {};
            if (showField('name') && values.name) {
                companyUpdate.name = values.name;
            }
            if (showField('size') && values.size) {
                companyUpdate.size = values.size;
            }
            if (showField('industry_sector') && values.industry_sector) {
                companyUpdate.industry_sector = values.industry_sector;
            }
            if (showField('annual_revenue') && values.annual_revenue) {
                companyUpdate.annual_revenue = values.annual_revenue;
            }
            if (showField('legal_structure') && values.legal_structure) {
                companyUpdate.legal_structure = values.legal_structure;
            }
            if (showField('address')) {
                if (values.country)
                    companyUpdate.country = values.country;
                if (values.province)
                    companyUpdate.province = values.province;
                if (values.city)
                    companyUpdate.city = values.city;
                if (values.address)
                    companyUpdate.address = values.address;
                if (values.postal_code)
                    companyUpdate.postal_code = values.postal_code;
                if (values.lat)
                    companyUpdate.lat = values.lat;
                if (values.lng)
                    companyUpdate.lng = values.lng;
            }
            await onSave(companyUpdate);
            onClose();
        }
        catch (_a) {
        }
        finally {
            setSaving(false);
        }
    }, [form, onSave, onClose, showField]);
    return (_jsx(Modal, { open: open, onCancel: onClose, footer: null, destroyOnHidden: true, width: 800, children: _jsx(Form, { form: form, layout: "vertical", children: _jsxs(ModalContent, { children: [_jsx(Typography.Title, { level: 4, elementTheme: "h6", noMargin: true, textAlign: "center", children: t('companyInfoModal|companyInfoModalTitle') }), _jsx(Typography, { elementTheme: "body2", textAlign: "center", children: t('companyInfoModal|companyInfoModalDescription') }), showField('name') && (_jsx(ModalFieldGroup, { children: _jsx(FormItem, { name: "name", label: _jsx(FormLabel, { label: t('profile|profileForm.company.labels.name') }), rules: [
                                {
                                    required: true,
                                    message: t('profile|profileForm.company.validation.name'),
                                },
                            ], children: _jsx(Input, { size: "large", placeholder: t('profile|profileForm.company.placeholder.name') }) }) })), showField('industry_sector') && (_jsx(ModalFieldGroup, { children: _jsx(FormItem, { name: "industry_sector", label: _jsx(FormLabel, { label: t('company|industry') }), rules: [
                                {
                                    required: true,
                                    message: t('profile|profileForm.company.validation.industrySector'),
                                },
                            ], children: _jsx(Select, { mode: "multiple", options: options === null || options === void 0 ? void 0 : options.industryOptions, placeholder: t('profile|profileForm.company.placeholder.industrySector'), maxCount: 3, size: "large" }) }) })), showField('address') && (_jsx(ModalFieldGroup, { children: _jsx(AddressAutocompleteInput, { form: form, showManualAddress: true, cityOnly: true }) })), showField('size') && (_jsx(ModalFieldGroup, { children: _jsx(FormItem, { name: "size", label: _jsx(FormLabel, { label: t('profile|profileForm.company.labels.size') }), rules: [
                                {
                                    required: true,
                                    message: t('profile|profileForm.company.validation.size'),
                                },
                            ], children: _jsx(Select, { options: options === null || options === void 0 ? void 0 : options.sizeOptions, placeholder: t('profile|profileForm.company.placeholder.size'), size: "large" }) }) })), showField('annual_revenue') && (_jsx(ModalFieldGroup, { children: _jsx(FormItem, { name: "annual_revenue", label: _jsx(FormLabel, { label: t('profile|profileForm.company.labels.annualRevenue') }), rules: [
                                {
                                    required: true,
                                    message: t('profile|profileForm.company.validation.annualRevenue'),
                                },
                            ], children: _jsx(Select, { options: options === null || options === void 0 ? void 0 : options.revenueOptions, placeholder: t('profile|profileForm.company.placeholder.annualRevenue'), size: "large" }) }) })), showField('legal_structure') && (_jsx(ModalFieldGroup, { children: _jsx(FormItem, { name: "legal_structure", label: _jsx(FormLabel, { label: t('companyForm|legalStructure') }), rules: [
                                {
                                    required: true,
                                    message: t('companyForm|legalStructureRequired'),
                                },
                            ], children: _jsx(Select, { options: options === null || options === void 0 ? void 0 : options.legalStructOptions, placeholder: t('companyForm|legalStructure'), size: "large" }) }) })), _jsxs(ModalFooter, { children: [_jsx(Button, { onClick: onClose, defaultStyle: theme.colors.white_1, children: t('button|cancel') }), _jsx(Button, { defaultStyle: theme.colors.purple_1, size: "small", loading: saving, onClick: handleSubmit, children: t('button|confirm') })] })] }) }) }));
};
export default CompanyInfoModal;
